import { zodResolver } from "@hookform/resolvers/zod";
import { Anchor, Button, Checkbox, Grid, Group, PasswordInput, TextInput } from "@mantine/core";
import { useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";
import { api } from "../../api/client";
import { useBrand } from "../../shared/brand/brand";
import { errorMessage } from "../../shared/session/errors";
import { saveOnboarding } from "../../shared/session/session";
import { FormError, Page } from "../../shared/ui/Page";

const schema = z
  .object({
    id_number: z.string().regex(/^[0-9 ]{6,14}$/),
    legal_name: z.string().min(3).max(120),
    date_of_birth: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    email: z.string().email(),
    phone: z.string().min(9).max(20),
    password: z.string().min(10).max(128),
    confirm_password: z.string(),
    terms: z.literal(true),
  })
  .refine((f) => f.password === f.confirm_password, { path: ["confirm_password"] });
type Form = z.infer<typeof schema>;

export function SignUpPage() {
  const { t, i18n } = useTranslation();
  const brand = useBrand();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const { register, handleSubmit, formState, control } = useForm<Form>({
    resolver: zodResolver(schema),
    mode: "onChange",
  });
  const req = t("form.required");
  // Create account stays disabled until the terms are accepted and the two
  // passwords are the same.
  const [password, confirm, terms] = useWatch({ control, name: ["password", "confirm_password", "terms"] });
  const passwordsMatch = !!password && password === confirm;
  const mismatch = !!confirm && confirm !== password;

  const onSubmit = handleSubmit(async (f) => {
    setError(null);
    const { data, error } = await api.POST("/auth/signup", {
      body: {
        id_number: f.id_number,
        legal_name: f.legal_name,
        date_of_birth: f.date_of_birth,
        email: f.email,
        phone: f.phone,
        password: f.password,
        locale: i18n.language,
        terms_version: brand.terms_version,
      },
    });
    if (!data) return setError(errorMessage(error, t("error.generic")));
    saveOnboarding(data.onboarding_token);
    navigate("/onboarding");
  });
  const e = formState.errors;
  return (
    // Two columns from tablet width, so the whole form fits a laptop screen;
    // one column on phones.
    <Page title={t("signup.title")} maw={760}>
      <form onSubmit={onSubmit} noValidate>
        <Grid gutter="sm">
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label={t("signup.id_number")}
              inputMode="numeric"
              autoComplete="off"
              required
              error={e.id_number && req}
              {...register("id_number")}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label={t("signup.legal_name")}
              autoComplete="name"
              required
              error={e.legal_name && req}
              {...register("legal_name")}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label={t("signup.date_of_birth")}
              type="date"
              required
              error={e.date_of_birth && req}
              {...register("date_of_birth")}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              label={t("signup.phone")}
              type="tel"
              autoComplete="tel"
              required
              error={e.phone && req}
              {...register("phone")}
            />
          </Grid.Col>
          <Grid.Col span={12}>
            <TextInput
              label={t("signup.email")}
              type="email"
              autoComplete="email"
              required
              error={e.email && req}
              {...register("email")}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <PasswordInput
              label={t("signup.password")}
              autoComplete="new-password"
              description={t("signup.password_hint")}
              required
              error={e.password && t("signup.password_hint")}
              {...register("password")}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6 }} style={{ alignSelf: "flex-end" }}>
            <PasswordInput
              label={t("signup.confirm_password")}
              autoComplete="new-password"
              required
              error={mismatch ? t("signup.password_mismatch") : undefined}
              {...register("confirm_password")}
            />
          </Grid.Col>
        </Grid>
        <FormError message={error} />
        {/* The terms and the button share a row from tablet width; stacked on phones. */}
        <Group justify="space-between" align="center" gap="sm" mt="md">
          <Checkbox label={t("signup.terms")} required error={e.terms && req} {...register("terms")} />
          <Button
            type="submit"
            w={{ base: "100%", sm: "auto" }}
            miw={200}
            loading={formState.isSubmitting}
            disabled={!terms || !passwordsMatch}
          >
            {t("signup.submit")}
          </Button>
        </Group>
      </form>
      <Group gap="lg" style={{ rowGap: 4 }}>
        <Anchor component={Link} to="/signin">
          {t("signup.have_account")}
        </Anchor>
        <Anchor component={Link} to="/onboarding/resume">
          {t("signup.resume")}
        </Anchor>
      </Group>
    </Page>
  );
}
